import React, { useEffect, useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

/** TrustBox bootstrap — same script as marketing pages; widget attrs match enterprise / homepage embeds. */
const TRUSTPILOT_SCRIPT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement | null, refresh?: boolean) => void;
    };
  }
}

export interface PricingTrustpilotWidgetProps {
  /** Extra top margin (e.g. compact layout in small modal) */
  compact?: boolean;
  /** Minimal vertical margin — use directly under Stripe / promo */
  dense?: boolean;
}

/**
 * Loads Trustpilot bootstrap once, then initializes the micro star rating widget in this container.
 */
const PricingTrustpilotWidget = ({ compact = false, dense = false }: PricingTrustpilotWidgetProps) => {
  const theme = useTheme();
  /** Trustpilot often injects a taller iframe on narrow viewports; 28px data-height is too small and breaks flow. */
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const init = () => {
      window.Trustpilot?.loadFromElement(el, true);
    };

    const existing = document.querySelector(
      `script[src="${TRUSTPILOT_SCRIPT_SRC}"]`
    ) as HTMLScriptElement | null;

    if (existing) {
      if (window.Trustpilot) {
        init();
      } else {
        existing.addEventListener('load', init);
        return () => existing.removeEventListener('load', init);
      }
      return undefined;
    }

    const script = document.createElement('script');
    script.src = TRUSTPILOT_SCRIPT_SRC;
    script.async = true;
    script.type = 'text/javascript';
    script.onload = init;
    document.body.appendChild(script);

    return undefined;
  }, [isNarrow]);

  const spacing = dense
    ? { mt: 0.5, mb: 0.5, px: { xs: 0, sm: 1 } }
    : compact
      ? { mt: 2, mb: 2, px: { xs: 1, sm: 2 } }
      : { mt: 4, mb: 3, px: { xs: 1, sm: 2 } };

  const widgetHeight = isNarrow ? '72px' : '28px';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
        overflow: 'visible',
        position: 'relative',
        zIndex: 1,
        // Reserve vertical space: Trustpilot iframe can be position:absolute inside; without this the next section overlaps.
        minHeight: dense ? { xs: 88, sm: 44 } : compact ? { xs: 72, sm: 48 } : { xs: 96, sm: 56 },
        ...spacing,
        '& .trustpilot-widget': {
          position: 'relative',
          display: 'block',
          minHeight: dense ? { xs: 72, sm: 36 } : 40,
        },
        '& .trustpilot-widget iframe': {
          maxWidth: '100% !important',
        },
      }}
    >
      <div
        ref={containerRef}
        className="trustpilot-widget"
        data-businessunit-id="5ee35a2bfa537d00010b5b18"
        data-locale="en-US"
        data-style-height={widgetHeight}
        data-style-width="100%"
        data-template-id="5419b6ffb0d04a076446a9af"
        data-theme="light"
        data-token="eb9ea9e1-5783-494f-9b8e-d024e3c6d629"
      >
        <a href="https://www.trustpilot.com/review/airbtics.com" rel="noopener noreferrer" target="_blank">
          Trustpilot
        </a>
      </div>
    </Box>
  );
};

export default PricingTrustpilotWidget;
